import { queryKeys } from '@query/keys'
import { useQuery, useQueryClient } from '@tanstack/react-query'
import { useCallback, useMemo } from 'react'
import { useTranslation } from 'react-i18next'

import { useAccountReadContext } from '@context/AccountContextContext'

import { SummaryItem } from '@ui/v2/TableSummary/TableSummary'

import { useApiClient } from '@utils/ApiClient'
import { ClientApiClient } from '@utils/clientApi'

import { FilterQueryProps } from '@model/FilterFields'

interface IBRebateClientsSummary {
  data: SummaryItem[]
  forceRefresh: () => void
  isLoading: boolean
}

export const useIBRebateClientsSummary = (search?: FilterQueryProps): IBRebateClientsSummary => {
  const apiClient = useApiClient(ClientApiClient)
  const queryClient = useQueryClient()
  const { account } = useAccountReadContext()
  const { t } = useTranslation()

  const ibId = account?.clientIntroducingBroker?.id

  const { data: summaryData, isLoading } = useQuery({
    queryKey: queryKeys.ib.rebates.clientsSummary(ibId, search as Record<string, unknown>),
    queryFn: () => apiClient.getIBTradingAccountsRebateSummary(ibId!, { search }),
    enabled: !!ibId,
  })

  const forceRefresh = useCallback(() => {
    queryClient.invalidateQueries({
      queryKey: queryKeys.ib.rebates.clientsSummary(ibId, search as Record<string, unknown>),
    })
  }, [queryClient, ibId, search])

  const data = useMemo<SummaryItem[]>(() => {
    return [
      {
        title: t('IB.RebatesPage.ClientsPage.Total TAs'),
        value: summaryData?.totalTas || 0,
      },
      {
        title: t('IB.RebatesPage.ClientsPage.TAs with rebates'),
        value: summaryData?.tasWithRebates || 0,
      },
      {
        title: t('IB.RebatesPage.ClientsPage.Approvals pending'),
        value: summaryData?.approvalsPending || 0,
      },
      {
        title: t('IB.RebatesPage.ClientsPage.Groups created'),
        value: summaryData?.groupsTotal || 0,
      },
    ]
  }, [summaryData, t])

  return { data, forceRefresh, isLoading }
}
